// Cross-tenant audit feed for Admin / Sales.
// Read-only — every action button lives on My Requests / Approvals
// where the per-user auth rules apply.

import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AzureAD } from 'react-aad-msal'
import { authProvider } from '../../../services/authProvider'
import store from '../../../store/configureStore'
import {
  Container, Row, Col, Input, Badge, Button,
} from 'reactstrap'
import PageContainer from '../../Shared/LayoutComponents/PageContainer'
import PageHeading from '../../Shared/LayoutComponents/PageHeading'
import LoadingSpinner from '../../Shared/LoadingSpinner'
import { fetchAuditLog } from '../../actions/cspLicensesActions'
import CSPLicenseDevPanel from './CSPLicenseDevPanel'
import { formatLocal } from '../../Shared/formatDateTime'
import BasketLineRow from './BasketLineRow'
import { REQUEST_STATUS_COLOR } from './requestStatusColor'
import { canUseLicenceFeature } from './featureAccess'

const STATUSES = ['Pending', 'Approved', 'Applied', 'Failed', 'Rejected', 'Cancelled']

const resolvedTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'local'
  } catch (_) {
    return 'local'
  }
}

const PAGE_SIZE = 50

// Default range = last 90 days. ISO 8601 dates (browser-local at midnight)
// so a user typing into a <input type="date"> doesn't surprise them with
// off-by-one timezone shifts.
const defaultFromDate = () => {
  const d = new Date()
  d.setDate(d.getDate() - 90)
  return d.toISOString().slice(0, 10)
}
const defaultToDate = () => new Date().toISOString().slice(0, 10)

const CSPLicenseActivityLogInner = () => {
  const dispatch = useDispatch()
  const auditLog = useSelector((s) => s.cspLicenses.auditLog) || { rows: [], totalCount: 0, page: 1, pageSize: PAGE_SIZE }
  const loading = useSelector((s) => s.cspLicenses.auditLogLoading)

  const [statuses, setStatuses] = useState([])
  const [from, setFrom] = useState(defaultFromDate)
  const [to, setTo] = useState(defaultToDate)
  const [requester, setRequester] = useState('')
  const [company, setCompany] = useState('')
  const [page, setPage] = useState(1)
  const [expandedId, setExpandedId] = useState(null)

  const load = (pageOverride) => {
    dispatch(fetchAuditLog({
      statuses,
      fromUtc: from ? `${from}T00:00:00.000Z` : null,
      toUtc: to ? `${to}T23:59:59.999Z` : null,
      requester,
      company,
      page: pageOverride || page,
      pageSize: PAGE_SIZE,
    }))
  }

  // Mount-only effect: kick off the initial fetch with default filters.
  // The deps array is intentionally empty - subsequent loads run via
  // explicit Apply / pagination handlers below.
  useEffect(() => {
    load(1)
    setPage(1)
  }, [])

  const onApplyFilters = () => {
    setPage(1)
    load(1)
  }

  const onPage = (delta) => {
    const totalPages = Math.max(1, Math.ceil((auditLog.totalCount || 0) / PAGE_SIZE))
    const next = Math.min(Math.max(1, page + delta), totalPages)
    setPage(next)
    load(next)
  }

  const toggleStatus = (s) => {
    setStatuses((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s])
  }

  const totalPages = Math.max(1, Math.ceil((auditLog.totalCount || 0) / PAGE_SIZE))
  const tz = resolvedTimeZone()

  return (
    <PageContainer>
      <Container fluid className="mt-3 mb-4">
        <Row>
          <Col>
            <Link to="/CSPLicenses" className="small">&larr; Back to 365 Licenses</Link>
            <PageHeading>Activity Log</PageHeading>
            <small className="text-muted">
              Every licence change request across all customers. Read-only.
            </small>
          </Col>
        </Row>

        {/* ─── Filter bar ─────────────────────────── */}
        <Row className="mt-3">
          <Col md={4}>
            <div className="small text-muted mb-1">Status</div>
            <div>
              {STATUSES.map((s) => (
                <Button
                  key={s}
                  size="sm"
                  color={statuses.includes(s) ? 'primary' : 'outline-secondary'}
                  className="mr-1 mb-1"
                  onClick={() => toggleStatus(s)}
                >
                  {s}
                </Button>
              ))}
            </div>
          </Col>
          <Col md={3}>
            <div className="small text-muted mb-1">Date range (local)</div>
            <div className="d-flex">
              <Input type="date" bsSize="sm" value={from}
                onChange={(e) => setFrom(e.target.value)} />
              <span className="mx-1">to</span>
              <Input type="date" bsSize="sm" value={to}
                onChange={(e) => setTo(e.target.value)} />
            </div>
          </Col>
          <Col md={2}>
            <div className="small text-muted mb-1">Requester</div>
            <Input type="text" bsSize="sm" placeholder="UPN or name"
              value={requester} onChange={(e) => setRequester(e.target.value)} />
          </Col>
          <Col md={2}>
            <div className="small text-muted mb-1">Customer</div>
            <Input type="text" bsSize="sm" placeholder="Company name"
              value={company} onChange={(e) => setCompany(e.target.value)} />
          </Col>
          <Col md={1} className="d-flex align-items-end">
            <Button color="primary" size="sm" onClick={onApplyFilters}>Apply</Button>
          </Col>
        </Row>

        {/* ─── Results ────────────────────────────── */}
        <Row className="mt-3">
          <Col>
            {loading ? (
              <LoadingSpinner />
            ) : (auditLog.rows || []).length === 0 ? (
              <div className="text-muted">No matching requests in this window.</div>
            ) : (
              <>
                <div className="small text-muted mb-2">
                  {auditLog.totalCount || 0} request(s) — page {page} of {totalPages}
                </div>
                <table className="table table-hover table-sm">
                  <thead>
                    <tr>
                      <th>Date &middot; {tz}</th>
                      <th>Requester</th>
                      <th>Customer</th>
                      <th>CW Ticket</th>
                      <th>Status</th>
                      <th className="text-right">SKUs</th>
                      <th className="text-right">Net Δ</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(auditLog.rows || []).map((r) => {
                      const isExpanded = expandedId === r.id
                      return (
                        <React.Fragment key={r.id}>
                          <tr style={{ cursor: 'pointer' }}
                            onClick={() => setExpandedId(isExpanded ? null : r.id)}
                          >
                            <td><small>{formatLocal(r.createdAt)}</small></td>
                            <td>
                              {r.requesterDisplayName || r.requesterUpn}
                              {r.selfApproved && (
                                <Badge color="info" className="ml-1" pill>self-approved</Badge>
                              )}
                            </td>
                            <td>{r.companyName}</td>
                            <td><code className="small">#{r.cwTicketId}</code></td>
                            <td>
                              <Badge color={REQUEST_STATUS_COLOR[r.status] || 'secondary'}>{r.status}</Badge>
                            </td>
                            <td className="text-right">{r.lineItemCount}</td>
                            <td className="text-right">
                              <strong>{r.netSeatDelta > 0 ? `+${r.netSeatDelta}` : r.netSeatDelta}</strong>
                            </td>
                            <td><small>{isExpanded ? '▼' : '▶'}</small></td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan={8} style={{ background: '#f6f8fa' }}>
                                <div className="px-2 py-2">
                                  {r.approverUpn && (
                                    <div className="small text-muted mb-2">
                                      {r.selfApproved ? 'Self-approved by' : 'Approver:'}{' '}
                                      <strong>{r.approverDisplayName || r.approverUpn}</strong>
                                      {r.appliedAt && (
                                        <> &middot; applied {formatLocal(r.appliedAt)}</>
                                      )}
                                    </div>
                                  )}
                                  <table className="table table-sm mb-2">
                                    <thead>
                                      <tr>
                                        <th>SKU</th>
                                        <th className="text-right">Before</th>
                                        <th className="text-right">After</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(r.lineItems || []).map((li) => (
                                        <BasketLineRow key={li.stockCode}
                                          skuDisplayName={li.skuDisplayName}
                                          stockCode={li.stockCode}
                                          stockDescription={li.stockDescription}
                                          previousQuantity={li.previousQuantity}
                                          requestedQuantity={li.requestedQuantity}
                                        />
                                      ))}
                                    </tbody>
                                  </table>
                                  <div>
                                    <Link to={`/CSPLicenses/MyRequests#${r.id}`} className="small">
                                      View full request →
                                    </Link>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      )
                    })}
                  </tbody>
                </table>

                {totalPages > 1 && (
                  <div className="d-flex justify-content-end align-items-center">
                    <Button size="sm" color="outline-secondary" className="mr-2"
                      disabled={page <= 1} onClick={() => onPage(-1)}>← Prev</Button>
                    <Button size="sm" color="outline-secondary"
                      disabled={page >= totalPages} onClick={() => onPage(1)}>Next →</Button>
                  </div>
                )}
              </>
            )}
          </Col>
        </Row>
      </Container>
      <CSPLicenseDevPanel />
    </PageContainer>
  )
}

const CSPLicenseActivityLog = () => (
  <AzureAD provider={authProvider} reduxStore={store}>
    {({ accountInfo }) => {
      const realRoles = accountInfo?.account?.idToken?.roles || []
      // Outer gate uses REAL AAD roles - this is the direct-URL
      // backstop. ITM/SSE-only users have no menu link to this page.
      // Feature gate AND this page's own audience. Activity Log is Admin +
      // Sales, narrower than the feature, so both have to hold: during the
      // Admin-only UAT this resolves to Admin, and when the feature widens to
      // all four roles it still resolves to Admin + Sales rather than silently
      // opening the cross-tenant audit feed to ITManager and SSE.
      // ANDed, not ORed - an OR here would widen the page at GA and leave the
      // link that reaches it (CSPLicenses.jsx) disagreeing with the page.
      const allowed = canUseLicenceFeature(realRoles)
        && (realRoles.includes('Admin') || realRoles.includes('Sales'))
      if (!allowed) {
        return (
          <PageContainer>
            <Container fluid className="mt-3">
              <p>You do not have permission to view this page.</p>
            </Container>
          </PageContainer>
        )
      }
      return <CSPLicenseActivityLogInner />
    }}
  </AzureAD>
)

export default CSPLicenseActivityLog
